import type React from "react";
import { ActionIcon, Group, Menu, Tooltip } from "@mantine/core";
import { Table } from "@xplatform/design-system";
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconDotsVertical,
  IconEdit,
  IconHistory,
  IconRotateClockwise,
  IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

interface RowActionsCellProps {
  expanded: boolean;
  totalAlts: number;
  isFailed: boolean;
  isSaving: boolean;
  actionsDisabled: boolean;
  hasTopMatch: boolean;
  /** Menu-only: adds the "clear edit" entry. */
  hasEdit?: boolean;
  /** Accent for the expand toggle — teal on the base table, blue on wd. */
  expandColor: string;
  /** Render the expand toggle inside this cell. wd gives the toggle its own
   * column, so it opts out and renders {@link ExpandToggle} separately. */
  showExpand?: boolean;
  /** Overflow menu (custom correction / history / clear edit). wd turns it
   * off: it takes TM's flatter shape instead — a plain history button, and
   * reject swapping to recover on an already-rejected row. */
  showMenu?: boolean;
  /** Only read when {@link showMenu} is false: drives the reject/recover swap. */
  isRejected?: boolean;
  onToggleExpand: () => void;
  onAccept: () => void;
  onReject: () => void;
  /** Menu-only: opens the custom-correction modal. */
  onOpenCustom?: () => void;
  onOpenHistory: () => void;
  onRecover: () => void;
}

/** Actions column shared by the base and wd result rows: expand toggle,
 * accept / reject, and the overflow menu (custom correction / history /
 * clear edit). */
const RowActionsCell: React.FC<RowActionsCellProps> = ({
  expanded,
  totalAlts,
  isFailed,
  isSaving,
  actionsDisabled,
  hasTopMatch,
  hasEdit,
  expandColor,
  showExpand = true,
  showMenu = true,
  isRejected = false,
  onToggleExpand,
  onAccept,
  onReject,
  onOpenCustom,
  onOpenHistory,
  onRecover,
}) => {
  const { t } = useTranslation();

  return (
    <Table.Td>
      <Group gap={4} wrap="nowrap" justify="flex-start">
        {showExpand && (
          <ExpandToggle
            expanded={expanded}
            totalAlts={totalAlts}
            isFailed={isFailed}
            expandColor={expandColor}
            onToggleExpand={onToggleExpand}
          />
        )}
        <Tooltip label={t("resultRow.actions.accept")}>
          <ActionIcon
            size="sm"
            variant="light"
            color="teal"
            onClick={onAccept}
            disabled={!hasTopMatch || actionsDisabled}
            loading={isSaving}
            aria-label={t("resultRow.actions.accept")}
          >
            <IconCheck size={14} />
          </ActionIcon>
        </Tooltip>
        {!showMenu && isRejected ? (
          <Tooltip
            label={t("resultRow.actions.recover", { defaultValue: "Recover" })}
          >
            <ActionIcon
              size="sm"
              variant="light"
              color="blue"
              onClick={onRecover}
              disabled={actionsDisabled}
              loading={isSaving}
              aria-label={t("resultRow.actions.recover", {
                defaultValue: "Recover",
              })}
            >
              <IconRotateClockwise size={14} />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Tooltip label={t("resultRow.actions.reject")}>
            <ActionIcon
              size="sm"
              variant="light"
              color="red"
              onClick={onReject}
              disabled={actionsDisabled}
              loading={isSaving}
              aria-label={t("resultRow.actions.reject")}
            >
              <IconX size={14} />
            </ActionIcon>
          </Tooltip>
        )}
        {showMenu ? (
          <Menu
            shadow="md"
            width={200}
            position="bottom-end"
            transitionProps={{ duration: 0 }}
          >
            <Menu.Target>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                aria-label={t("resultRow.actions.more", {
                  defaultValue: "More actions",
                })}
              >
                <IconDotsVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                onClick={onOpenCustom}
                disabled={actionsDisabled}
              >
                {t("resultRow.actions.customCorrection")}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconHistory size={14} />}
                onClick={onOpenHistory}
              >
                {t("resultRow.actions.history")}
              </Menu.Item>
              {hasEdit && (
                <Menu.Item
                  leftSection={<IconRotateClockwise size={14} />}
                  onClick={onRecover}
                  disabled={isSaving}
                >
                  {t("resultRow.actions.clearEdit")}
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Tooltip label={t("resultRow.actions.history")}>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="blue"
              onClick={onOpenHistory}
              aria-label={t("resultRow.actions.history")}
            >
              <IconHistory size={14} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Table.Td>
  );
};

export default RowActionsCell;

interface ExpandToggleProps {
  expanded: boolean;
  totalAlts: number;
  isFailed: boolean;
  expandColor: string;
  /** Override the ActionIcon variant. Defaults to a filled/light button look;
   * wd passes "transparent" for a plain chevron with no button background. */
  variant?: string;
  onToggleExpand: () => void;
}

/** The alternatives expand/collapse chevron. Shared by {@link RowActionsCell}
 * and wd's dedicated dropdown column. */
export const ExpandToggle: React.FC<ExpandToggleProps> = ({
  expanded,
  totalAlts,
  isFailed,
  expandColor,
  variant,
  onToggleExpand,
}) => {
  const { t } = useTranslation();
  const viewAlternativesLabel = t("jobResultsPage.actions.viewAlternatives", {
    count: totalAlts,
    defaultValue: `View ${totalAlts} matches`,
  });
  return (
    <Tooltip
      label={
        expanded
          ? t("jobResultsPage.actions.hideAlternatives", {
              defaultValue: "Hide alternatives",
            })
          : viewAlternativesLabel
      }
    >
      <ActionIcon
        size="sm"
        variant={variant ?? (expanded ? "filled" : "light")}
        color={expandColor}
        onClick={onToggleExpand}
        disabled={totalAlts === 0 || isFailed}
        aria-label={viewAlternativesLabel}
      >
        {expanded ? (
          <IconChevronDown size={14} />
        ) : (
          <IconChevronRight size={14} />
        )}
      </ActionIcon>
    </Tooltip>
  );
};
